const axios = require('axios')
const { objectToCamelCase, rasterizeErrors, payloadToSnakeCase } = require('./response.utils')

const optionsEnum = {
  GET: 'GET',
  POST: 'POST',
  DELETE: 'DELETE',
  PUT: 'PUT',
  PATCH: 'PATCH',
}

const getHost = env => (env === 'prod' ? `https://${env}.doctoroncall.com` : `https://api.${env}.doctoroncall.com`)

class API {
  constructor(env) {
    this.optionsObject = {
      headers: {},
      transformCamelCase: false,
    }
    this.client = axios.create({
      baseURL: getHost(env),
      timeout: 30000,
    })
    this.service = null
  }

  setMethod() {
    const assign = method => {
      this.optionsObject.method = method
      this.optionsObject.body = null
      return this
    }
    return {
      get: () => assign(optionsEnum.GET),
      post: () => assign(optionsEnum.POST),
      delete: () => assign(optionsEnum.DELETE),
      put: () => assign(optionsEnum.PUT),
      patch: () => assign(optionsEnum.PATCH),
    }
  }

  setBody(body) {
    this.optionsObject.body = JSON.stringify(body)
    return this
  }

  setCamelCaseTransformer(bool) {
    this.optionsObject.transformCamelCase = bool
    return this
  }

  addHeader(key, value) {
    this.optionsObject.headers[`${key}`] = value
    return this
  }

  setAuthorizationToken(token) {
    this.addHeader('Authorization', `Bearer ${token}`)
    return this
  }

  setAPIKey(key) {
    if (this.optionsObject.headers.Authorization) {
      throw new Error('Authorisation token is already set')
    }
    this.addHeader('x-api-key', key)
    return this
  }

  setService(name) {
    this.service = name
    return this
  }

  _formatResponse(data) {
    if (Array.isArray(data)) {
      return data.map(objectToCamelCase)
    } if (typeof data === 'object') {
      return objectToCamelCase(data)
    }

    return data
  }

  async process(endpoint, params = {}) {
    if (!this.optionsObject.headers['Content-Type']) {
      this.optionsObject.headers['Content-Type'] = 'application/json'
    }

    const url = `/${this.service || ''}${endpoint}`
    const options = {}

    if (this.optionsObject.body) {
      const data = this.optionsObject.transformCamelCase
        ? payloadToSnakeCase(this.optionsObject.body)
        : this.optionsObject.body
      options.data = JSON.parse(
        JSON.stringify(data),
      )
    }

    if (params) {
      const data = this.optionsObject.transformCamelCase ? payloadToSnakeCase(params) : params
      options.params = JSON.parse(JSON.stringify(data))
    }

    try {
      const request = await this.client.request({
        method: this.optionsObject.method,
        url,
        headers: this.optionsObject.headers,
        ...options,
      })
      request.data = this.optionsObject.transformCamelCase
        ? this._formatResponse(request.data) : request.data

      return request
    } catch (e) {
      const data = (e.response && e.response.data) || {}
      const error = {
        ...data,
        code: e.response ? e.response.status : e.status,
        message: e.response ? (data.message || e.response.statusText || e.message) : e.message,
      }
      throw rasterizeErrors(error)
    }
  }

  async processFile(endpoint, fileId, params = {}) {
    if (!this.optionsObject.headers['Content-Type']) {
      this.optionsObject.headers['Content-Type'] = 'application/json'
    }

    const url = `/${this.service || ''}${endpoint}`
    const options = {}

    if (this.optionsObject.body) {
      const data = this.optionsObject.transformCamelCase
        ? payloadToSnakeCase(this.optionsObject.body)
        : this.optionsObject.body
      options.data = JSON.parse(
        JSON.stringify(data),
      )
    }

    if (params) {
      const data = this.optionsObject.transformCamelCase ? payloadToSnakeCase(params) : params
      options.params = JSON.parse(JSON.stringify(data))
    }

    try {
      const request = await this.client.request({
        method: this.optionsObject.method,
        url,
        headers: this.optionsObject.headers,
        ...options,
      })
      request.data = this.optionsObject.transformCamelCase
        ? this._formatResponse(request.data) : request.data

      return { request, fileId }
    } catch (e) {
      const data = (e.response && e.response.data) || {}
      const error = {
        ...data,
        code: e.response ? e.response.status : e.status,
        message: e.response ? (data.message || e.response.statusText || e.message) : e.message,
        fileId,
      }
      throw rasterizeErrors(error)
    }
  }
}

module.exports = API
